import { InsuranceDocument, InsuranceStatus } from '@centecomm/portal-service';
import { useInsurance } from '@centecomm/portal-service-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InsuranceActions } from '../actions';
import selectors from '../selectors';

type UseInsuranceDocumentsStateHook = (insuranceId: number | string) => {
    addDocument: (occupantId: number | string, document: File) => Promise<InsuranceDocument[]>;
    addPendingDocument: (occupantId: number | string, document: File) => void;
    addPendingDocuments: (occupantId: number | string, documents: File[]) => void;
    areDocumentsLoaded: boolean;
    areDocumentsPending: boolean;
    documentsList: InsuranceDocument[];
    loadDocuments: (occupantId: number | string) => Promise<void>;
    removeDocument: (occupantId: number | string, documentId: string) => Promise<void>;
    removePendingDocument: (document: File) => void;
    hasPendingUploads: boolean;
    pendingDocumentCount: number;
};

const useInsuranceDocumentsState: UseInsuranceDocumentsStateHook = (insuranceId) => {
    const dispatch = useDispatch();

    const { addInsuranceDocument, deleteInsuranceDocument, getInsuranceDocuments } = useInsurance();

    const [pendingInsuranceDocuments, setPendingInsuranceDocuments] = useState<Record<number, boolean>>([]);

    const areDocumentsLoaded = useSelector(selectors.areDocumentsLoaded(insuranceId));
    const hasPendingUploads = useSelector(selectors.hasPendingUploads(insuranceId));
    const pendingDocumentCount = useSelector(selectors.pendingUploadCount(insuranceId));

    const areDocumentsPending =
        Object.hasOwnProperty.call(pendingInsuranceDocuments, insuranceId) && pendingInsuranceDocuments[insuranceId];

    const loadDocuments = async (occupantId: number | string): Promise<void> => {
        setPendingInsuranceDocuments({
            ...pendingInsuranceDocuments,
            [insuranceId]: true,
        });

        if (!Object.hasOwnProperty.call(pendingInsuranceDocuments, occupantId)) {
            const documents = await getInsuranceDocuments(+occupantId, +insuranceId);

            dispatch({
                type: InsuranceActions.ADD_INSURANCE_DOCUMENTS,
                payload: {
                    documents,
                    insuranceId,
                },
            });

            setPendingInsuranceDocuments({
                ...pendingInsuranceDocuments,
                [insuranceId]: false,
            });
        }
    };

    const addDocument = async (occupantId: number | string, document: File): Promise<InsuranceDocument[]> => {
        setPendingInsuranceDocuments({
            ...pendingInsuranceDocuments,
            [insuranceId]: true,
        });

        await addInsuranceDocument(occupantId, insuranceId, document);

        const documents = await getInsuranceDocuments(+occupantId, +insuranceId);

        dispatch({
            type: InsuranceActions.ADD_INSURANCE_DOCUMENTS,
            payload: {
                insuranceId,
                documents,
            },
        });

        dispatch({
            type: InsuranceActions.EDIT_INSURANCE_STATUS,
            payload: {
                insuranceId,
                occupantId,
                status: InsuranceStatus.NotSubmitted,
            },
        });

        setPendingInsuranceDocuments({
            ...pendingInsuranceDocuments,
            [insuranceId]: false,
        });

        return documents;
    };

    const removeDocument = async (occupantId: number | string, documentId: string) => {
        setPendingInsuranceDocuments({
            ...pendingInsuranceDocuments,
            [insuranceId]: true,
        });

        await deleteInsuranceDocument(occupantId, insuranceId, documentId);

        dispatch({
            type: InsuranceActions.REMOVE_INSURANCE_DOCUMENT,
            payload: {
                insuranceId,
                documentId,
            },
        });

        dispatch({
            type: InsuranceActions.EDIT_INSURANCE_STATUS,
            payload: {
                insuranceId,
                occupantId,
                status: InsuranceStatus.NotSubmitted,
            },
        });

        setPendingInsuranceDocuments({
            ...pendingInsuranceDocuments,
            [insuranceId]: false,
        });
    };

    const documentsList = useSelector(selectors.insuranceDocuments(insuranceId));

    const addPendingDocuments = (occupantId: number | string, documents: File[]) => {
        dispatch({
            type: InsuranceActions.ADD_PENDING_DOCUMENTS,
            payload: {
                documents,
                insuranceId,
                occupantId,
            },
        });
    };

    const addPendingDocument = (occupantId: number | string, document: File) => {
        dispatch({
            type: InsuranceActions.ADD_PENDING_DOCUMENT,
            payload: {
                document,
                insuranceId,
                occupantId,
            },
        });
    };

    const removePendingDocument = (document: File) => {
        dispatch({
            type: InsuranceActions.REMOVE_PENDING_DOCUMENT,
            payload: {
                document,
                insuranceId,
            },
        });
    };

    return {
        addDocument,
        addPendingDocument,
        addPendingDocuments,
        areDocumentsLoaded,
        areDocumentsPending,
        documentsList,
        loadDocuments,
        removeDocument,
        removePendingDocument,
        hasPendingUploads,
        pendingDocumentCount,
    };
};

export default useInsuranceDocumentsState;
